import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { WeeklyRecord } from '../services/weeklyRecordsService';

interface WellnessBarChartProps {
  data?: WeeklyRecord[];
  loading?: boolean;
  error?: string | null;
}

// Helper function to format weekly records data for the chart
const formatChartData = (weeklyRecords: WeeklyRecord[]) => {
  return weeklyRecords
    .sort((a, b) => new Date(a.recordDate).getTime() - new Date(b.recordDate).getTime())
    .map((record) => {
      // Format date as dd/mm/YY
      const date = new Date(record.recordDate);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      const weekLabel = `${day}/${month}/${year}`;
      
      return {
        week: weekLabel,
        digestion: record.digestion,
        energy: record.energy,
        sleep: record.sleep,
        avg: Math.round(record.averageScore * 10) / 10, // Round to 1 decimal
        fullDate: record.recordDate
      };
    });
};

const WellnessBarChart: React.FC<WellnessBarChartProps> = ({ 
  data = [], 
  loading = false, 
  error = null 
}) => {
  // Show loading state
  if (loading) {
    return (
      <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>Cargando datos de bienestar...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#e53e3e', fontSize: 14, textAlign: 'center' }}>
          <div>Error al cargar datos</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{error}</div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>No hay datos de bienestar disponibles</div>
      </div>
    );
  }

  const chartData = formatChartData(data);

  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="week" />
          <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} />
          <Tooltip 
            formatter={(value, name) => [value, name]}
            labelFormatter={(label) => {
              const item = chartData.find(d => d.week === label);
              return item ? `Fecha: ${item.fullDate}` : label;
            }}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Legend />
          <Bar dataKey="digestion" fill="#38b2ac" name="Digestión" />
          <Bar dataKey="energy" fill="#4fd1c5" name="Energía" />
          <Bar dataKey="sleep" fill="#81e6d9" name="Sueño" />
          <Line 
            type="monotone" 
            dataKey="avg" 
            stroke="#2c7a7b" 
            strokeWidth={3} 
            dot={{ r: 4, fill: '#2c7a7b' }}
            activeDot={{ r: 6, fill: '#2c7a7b' }}
            name="Promedio" 
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WellnessBarChart; 